import React, { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { SalvButton, SalvModal, SalvRadio, SalvRadioGroup, Size } from '@salvapp/ui-components';

export enum Template {
  QA = 'QA',
  AUDIT = 'AUDIT',
}

export const AlertFilterResultsExportModal = ({
  onExport,
  disabled,
}: {
  onExport: (value: Template) => void;
  disabled?: boolean;
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [template, setTemplate] = useState<Template | null>(null);

  const submit = () => {
    onExport(template!);
    setShowModal(false);
    setShowSuccessModal(true);
  };

  const bulletPoints = (points: string[]) => {
    return (
      <ul>
        {points.map((point) => (
          <li key={point}>
            <span className={'m-x-8'}>•</span>
            {point}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <div className='p-l-8'>
        <SalvButton
          label='Export alerts'
          size={Size.SMALL}
          variant='muted'
          disabled={disabled}
          onClick={() => {
            setShowModal(true);
            setTemplate(null);
          }}
        />
      </div>
      <SalvModal show={showModal} onHide={() => setShowModal(false)} className='alert-filters-results-export'>
        <SalvModal.Header closeButton>
          <span className='title-s'>Export selected alerts</span>
        </SalvModal.Header>
        <SalvModal.Body>
          <Container>
            <Row>
              <Col className='ps-0'>
                Choose the template you&apos;d like to use for exporting alerts. Below is an overview of the data that
                each template includes. The exported file will be in .csv format. <br />
                For a detailed breakdown,{' '}
                <a href='https://help.salv.com/en/articles/143277-alerts-view' target='_blank' rel='noreferrer'>
                  read here
                </a>
                . Note: there is a limit of <b>200,000</b> alerts per export. If you need to export more, you can split
                your alert filters using filtering conditions (e.g., by date ranges).
              </Col>
            </Row>
            <Row className='mt-3'>
              <SalvRadioGroup
                ariaLabel='template-radios'
                direction='horizontal'
                value={template}
                className='flex-grow-1'
              >
                <Col
                  className={template === Template.QA ? 'selected-card' : 'clickable-card'}
                  onClick={() => {
                    setTemplate(Template.QA);
                  }}
                >
                  <Row>
                    <SalvRadio value={Template.QA} label={'QA/QC template'} />
                  </Row>
                  <Row className='text-xs ps-5 py-2'>
                    <Col>
                      <div className={'text-strong-xs'}>Universal</div>
                      {bulletPoints([
                        'Alert ID',
                        'Person ID',
                        'Transaction ID',
                        'Alert Creation Time',
                        'Alert Status',
                        'Alert Status Start Time',
                        'Alert Status End Time',
                        'Alert Status Set by',
                        'Alert Details',
                        'Note with Alert Status',
                      ])}
                    </Col>
                  </Row>
                  <Row className='text-xs ps-5 py-2'>
                    <Col>
                      <div className={'text-strong-xs'}>+ Screening alerts</div>
                      {bulletPoints(['Screened Field', 'Screened Value'])}
                    </Col>
                  </Row>
                  <Row className='text-xs ps-5 py-2'>
                    <Col>
                      <div className={'text-strong-xs'}>+ Manual alerts</div>
                      {bulletPoints(['Rationale', 'Rationale Comment'])}
                    </Col>
                  </Row>
                </Col>
                <Col
                  className={`flex-grow-2 ${template === Template.AUDIT ? 'selected-card' : 'clickable-card'}`}
                  onClick={() => {
                    setTemplate(Template.AUDIT);
                  }}
                >
                  <Row>
                    <SalvRadio value={Template.AUDIT} label={'Audit template'} />
                  </Row>
                  <Row className='text-xs ps-5 py-2'>
                    <Col>
                      <div className={'text-strong-xs'}>Universal</div>
                      {bulletPoints([
                        'Alert ID',
                        'Alert Type',
                        'Alerted Entity Type',
                        'Person ID',
                        'Person Name',
                        'Transaction ID',
                        'Alert Creation Time',
                        'Alert Closing Time',
                        'Alert Closing Status',
                        'Alert Closed by',
                        'Alert Closing Note',
                        'Alert Details',
                      ])}
                    </Col>
                  </Row>
                  <Row className='text-xs ps-5 py-2'>
                    <Col>
                      <div className={'text-strong-xs'}>+ Monitoring alerts</div>
                      {bulletPoints([
                        'Transaction Timestamp',
                        'Transaction Amount',
                        'Transaction Currency',
                        'Transaction Reference',
                        'Transaction Sender Name',
                        'Transaction Receiver Name',
                        'Transaction Sender Bank Name',
                        'Transaction Receiver Bank Name',
                      ])}
                    </Col>
                    <Col>
                      <div className={'text-strong-xs'}>+ Transaction Screening alerts</div>
                      {bulletPoints([
                        'Screened Field',
                        'Screened Value',
                        'Transaction Timestamp',
                        'Transaction Amount',
                        'Transaction Currency',
                        'Transaction Reference',
                        'Transaction Sender Name',
                        'Transaction Receiver Name',
                        'Transaction Sender Bank Name',
                        'Transaction Receiver Bank Name',
                      ])}
                    </Col>
                    <Col>
                      <div className={'text-strong-xs'}>+ Person Screening alerts</div>
                      {bulletPoints(['Screened Field', 'Screened Value'])}
                    </Col>
                    <Col>
                      <div className={'text-strong-xs'}>+ Manual alerts</div>
                      {bulletPoints(['Rationale', 'Rationale Comment'])}
                    </Col>
                  </Row>
                </Col>
              </SalvRadioGroup>
            </Row>
          </Container>
        </SalvModal.Body>
        <SalvModal.Footer>
          <SalvButton
            label='Close'
            ariaLabel='close-button'
            variant='muted'
            onClick={() => setShowModal(false)}
            size={Size.LARGE}
          />
          <SalvButton
            label='Export'
            ariaLabel='export-button'
            disabled={!template}
            onClick={submit}
            variant='primary'
            size={Size.LARGE}
          />
        </SalvModal.Footer>
      </SalvModal>
      <SalvModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
        <SalvModal.Header closeButton>Export in progress</SalvModal.Header>
        <SalvModal.Body>
          Your export file is now being processed. The time required to prepare this export varies based on the number
          of alerts.
          <br />A download link will be sent directly to your email once the export is ready.
        </SalvModal.Body>
        <SalvModal.Footer>
          <SalvButton
            label='Close'
            ariaLabel='close-button'
            variant='muted'
            onClick={() => setShowSuccessModal(false)}
            size={Size.LARGE}
          />
        </SalvModal.Footer>
      </SalvModal>
    </>
  );
};
